import { useMemo } from "react";
import { useSelector } from "react-redux";
import { RootState } from "../store";
import { Placement } from "../types/Placement";
import { filterAndSort } from "../services/filterResults";
import { useBoundaryTagsByPlacementId } from "./useBoundary";
import { FieldState } from "src/store/filter";
import { SearchSliceType } from "src/store/search";

function searchIsInitializing(search: SearchSliceType) {
  return (
    search.result.isSearching &&
    search.result.text === "" &&
    search.result.placementIDs.length === 0
  );
}
export const usePlacementResults = (): Placement[] | null => {
  const placements = useSelector((state: RootState) => state.placements);
  const config = useSelector((state: RootState) => state.config);
  const filter = useSelector((state: RootState) => state.filter);
  const search = useSelector((state: RootState) => state.search);
  const sort = useSelector((state: RootState) => state.sort);
  const distance = useSelector((state: RootState) => state.distance);
  const latLngBounds = useSelector((state: RootState) => state.latLngBounds);
  const eligibilityToggle = useSelector(
    (state: RootState) => state.eligibilityToggle
  );
  const boundaryTagsByPlacementId = useBoundaryTagsByPlacementId(
    placements.status === "finished" ? placements.byId : undefined
  );

  const placementResults = useMemo(() => {
    if (placements.status !== "finished" || config.state !== "finished")
      return null;

    const searchResultPlacementIds: string[] = searchIsInitializing(search)
      ? Object.keys(placements.byId)
      : search.result.placementIDs;

    const placementsList: Placement[] = searchResultPlacementIds
      .map((id) => {
        return placements.byId[id] || ({} as Placement);
      })
      .filter((id) => id !== undefined);

    const filtersList = Object.keys(filter.byFieldName).map(
      (fieldName) => filter.byFieldName[fieldName] || ({} as FieldState)
    );

    const filtered = filterAndSort(
      placementsList,
      config.values,
      filtersList,
      sort.type,
      distance.distanceToPlacementById,
      latLngBounds.bounds,
      boundaryTagsByPlacementId
    );

    if (
      config.values.eligibility?.enableShowHideEligible &&
      placements.eligibilityEnabled &&
      eligibilityToggle.hideIneligible
    ) {
      return filtered.filter(
        (placement) => placement.eligibility === "eligible"
      );
    }

    return filtered;
  }, [
    placements,
    config,
    filter,
    search,
    distance,
    sort,
    latLngBounds,
    boundaryTagsByPlacementId,
    eligibilityToggle,
  ]);

  return placementResults;
};
